import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import {
  ICardUI,
  INewCompletedAccountCardsState,
} from 'src/app/api/interfaces/card.interface';
import { specialityType } from 'src/app/api/interfaces/configuration.interface';
import {
  IDefaultPagination,
  IPubBuilding,
  IPubChallengeActive,
  IPubChallengeHistory,
  IPubDailyQuest,
  IPubResultUI,
} from 'src/app/api/interfaces/pub.interface';
import { ICompletedCharacterSlots } from 'src/app/store/characterState/characterState.reducer';
import {
  IMainState,
  selectMainState,
} from 'src/app/store/mainState/mainState.reducer';

@Injectable({
  providedIn: 'root',
})
export class PubService {
  public pubBuilding: BehaviorSubject<IPubBuilding | undefined> =
    new BehaviorSubject<IPubBuilding | undefined>(undefined);

  public dailyQuestsList: IPubDailyQuest[] = [];

  public pickedDailyQuestSpeciality: specialityType = '';

  public currentQuestId: string | undefined;

  public currentQuestSlotIndex: number | undefined;

  public characterInUse: number[] = [];

  public tradingPickedCharacter: BehaviorSubject<
    ICompletedCharacterSlots | undefined
  > = new BehaviorSubject<ICompletedCharacterSlots | undefined>(undefined);

  public foodTradingChallengesActive: BehaviorSubject<
    IPubChallengeActive | undefined
  > = new BehaviorSubject<IPubChallengeActive | undefined>(undefined);

  public foodTradingChallengesHistory: BehaviorSubject<
    IDefaultPagination<IPubChallengeHistory> | undefined
  > = new BehaviorSubject<IDefaultPagination<IPubChallengeHistory> | undefined>(
    undefined
  );
  public updateChallengesList: BehaviorSubject<null | true> =
    new BehaviorSubject<null | true>(null);
  public tapListPickedCharacter: BehaviorSubject<
    ICompletedCharacterSlots | undefined
  > = new BehaviorSubject<ICompletedCharacterSlots | undefined>(undefined);

  public newDayUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  public currentResultData: BehaviorSubject<
    | { characterSlot?: ICompletedCharacterSlots; result: IPubResultUI }
    | undefined
  > = new BehaviorSubject<
    | { characterSlot?: ICompletedCharacterSlots; result: IPubResultUI }
    | undefined
  >(undefined);

  constructor(private state: Store) {}

  public updateDailyQuestList(dailyTask: IPubDailyQuest): void {
    const oldPubValues = this.pubBuilding.getValue();
    if (dailyTask && oldPubValues) {
      const newTaskList = oldPubValues.dailyTasks.map((task) => {
        if (
          (task && dailyTask && task.id === dailyTask.id) ||
          dailyTask.configuration.id === task.configuration.id
        ) {
          return {
            ...dailyTask,
            configuration: { ...task.configuration },
            status: dailyTask.status,
          };
        } else {
          return task;
        }
      });
      this.pubBuilding.next({ ...oldPubValues, dailyTasks: newTaskList });
    }
  }

  public checkSpecialityNewLevel(
    refreshedCharacter: ICompletedCharacterSlots | undefined | null,
    prevCharacter: ICompletedCharacterSlots | undefined | null,
    speciality: specialityType | undefined
  ): boolean {
    if (
      refreshedCharacter &&
      refreshedCharacter.character &&
      prevCharacter &&
      prevCharacter?.character &&
      speciality
    ) {
      const refreshedCharacterSpecialityLevel =
        refreshedCharacter.character.specialities.find(
          (characterSpeciality) =>
            characterSpeciality.config?.speciality === speciality
        )?.speciality.level;
      const prevCharacterSpecialityLevel =
        prevCharacter.character.specialities.find(
          (characterSpeciality) =>
            characterSpeciality.config?.speciality === speciality
        )?.speciality.level;
      if (refreshedCharacterSpecialityLevel && prevCharacterSpecialityLevel) {
        return (
          refreshedCharacterSpecialityLevel !== prevCharacterSpecialityLevel
        );
      }
    }
    return false;
  }

  public getUserCardQuantityOrderAndChallenges(
    userCards: INewCompletedAccountCardsState[],
    id: number
  ): number {
    return userCards.filter(
      (card) => card.card.status === 'ACTIVE' && card.card.cardId === id
    ).length;
  }
  public filterDuplicated(dailyQuests: IPubDailyQuest[]): IPubDailyQuest[] {
    return dailyQuests.reduce((allRecipes: IPubDailyQuest[], recipe) => {
      if (
        !allRecipes
          .map((recipes) => recipes.configuration.id)
          .includes(recipe.configuration.id)
      ) {
        allRecipes.push(recipe);
      }
      return allRecipes;
    }, []);
  }
  public createDailyQuestList(
    characterSlots: ICompletedCharacterSlots[],
    dailyTasks: IPubDailyQuest[]
  ): any {
    const completedDailyTaskList = this.filterDuplicated(
      dailyTasks.filter(
        (task) =>
          task.status === 'CREATED' ||
          task.status === 'IN_PROCESS' ||
          task.status === 'DONE' ||
          task.status === 'CANCELED' ||
          task.status !== 'COLLECTED'
      )
    ).map((task) => {
      let preparedCharacterIds: any = [];

      const characterWithSpecialitySlots =
        task.configuration.required.specialities.map((taskSlot) => {
          const characterSlot = characterSlots
            .filter((slot) => slot.character)
            .find(
              (slot) =>
                slot.character?.id ===
                task.characterIds[taskSlot.sequenceNumber]
            );
          preparedCharacterIds.push(characterSlot?.character?.id);

          return {
            ...taskSlot,
            pickedCharacterSlot: characterSlot,
          };
        });
      const canStart = characterWithSpecialitySlots.map((slot) => {
        if (slot.pickedCharacterSlot && slot.pickedCharacterSlot?.character) {
          return task.configuration.required.specialities.map(
            (reqSpeciality) => {
              const neededCharacterSpeciality =
                slot.pickedCharacterSlot?.character?.specialities.find(
                  (speciality) =>
                    speciality.config?.speciality === reqSpeciality.name
                );
              if (neededCharacterSpeciality) {
                return (
                  neededCharacterSpeciality.speciality.level >=
                  reqSpeciality.level
                );
              } else {
                return [false];
              }
            }
          );
        } else {
          return [false];
        }
      });

      return {
        ...task,
        characterIds: preparedCharacterIds,
        canStartTask: !canStart
          .reduce((acc, val) => acc.concat(val), [])
          .includes(false),
        configuration: {
          ...task.configuration,
          required: {
            ...task.configuration.required,
            specialities: characterWithSpecialitySlots,
          },
        },
      };
    });
    this.characterInUse = dailyTasks
      .filter((task) => task.status !== 'COLLECTED')
      .map((task) => {
        return task.characterIds;
      })
      .reduce((acc, val) => acc.concat(val), []);
    this.dailyQuestsList = completedDailyTaskList;

    let mainState: IMainState;
    let questListCardInstance: ICardUI[] | any;
    let questList: IPubDailyQuest[] = completedDailyTaskList;

    this.state
      .select(selectMainState)
      .subscribe((state) => (mainState = state));
    questListCardInstance = questList.map((el) => {
      return mainState.cards.api.cards.find(
        (card: { id: number }) => card.id === el.configuration.result.card.id
      );
    });
    questList = questList.map((el) => {
      var idx = questList.indexOf(el);
      return {
        ...el,
        bulletingBoardCard: questListCardInstance[idx],
      };
    });

    return questList;
  }
}
